import AccountCircleOutlined from "@mui/icons-material/AccountCircleOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { Link, Tooltip, Typography } from "@mui/material";
import clsx from "clsx";
import { uniqBy } from "lodash";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";

import { Button } from "@citrine/atoms/button";
import { CatalystIcon } from "@citrine/atoms/icons";
import { Spinner } from "@citrine/atoms/loading";
import type { DocumentMetadata } from "@citrine/resources/catalyst";
import {
  isMessageResponse,
  isUnsupportedResponse,
} from "@citrine/resources/catalyst";
import { DocumentClient } from "@citrine/resources/document";

import type { CatalystMode } from "#src/contexts/catalyst";

import * as sx from "./questions-and-answers.sx";
import type {
  CatalystQuestionAndAnswer,
  LiteratureQuestionAndAnswer,
  ModeChangeStatement,
  ModelQuestionAndAnswer,
  ModifiedConfigModelQuestionAndAnswer,
  PredictorChangeHandlers,
  SwitchToModelExpertPrompt,
  SwitchToModelExpertPromptHandlers,
} from "./types";
import {
  isLiteratureQuestionAndAnswer,
  isModeChangeStatement,
  isModelQuestionAndAnswer,
  isModifiedConfigModelQuestionAndAnswer,
  isSwitchToModelExpertPrompt,
} from "./types";
import { TEST_IDS } from "./utils";

function Question({ question }: { question: ReactNode }) {
  return (
    <sx.Question>
      <AccountCircleOutlined />
      <sx.Border>
        <sx.Body>
          <sx.Text variant="body1" component="span">
            {question}
          </sx.Text>
        </sx.Body>
      </sx.Border>
    </sx.Question>
  );
}

function AnswerReference({ metadata }: { metadata: DocumentMetadata }) {
  const text = `${metadata.source_type}:${metadata.source}`;
  const { data: privateDownloadData } =
    DocumentClient.getInstance().useGetDownloadLink(metadata.id ?? "", {
      enabled: metadata.source_type === "private",
    });
  switch (metadata.source_type) {
    case "doi":
      return (
        <Link href={`https://doi.org/${metadata.source}`} target="_blank">
          {text}
        </Link>
      );
    case "https":
      return (
        <Link href={`https:${metadata.source}`} target="_blank">
          {`https:${metadata.source}`}
        </Link>
      );
    case "private":
      return (
        <Link href={privateDownloadData?.url} download={metadata.source}>
          {metadata.source}
        </Link>
      );
    default:
      return <>{text}</>;
  }
}

function Message({ children }: { children: ReactNode }) {
  return (
    <sx.Text variant="body1" component="div">
      {children}
    </sx.Text>
  );
}

function Answer({
  additionalContent,
  error,
  message,
  mode,
}: {
  additionalContent?: ReactNode;
  error?: boolean;
  message: ReactNode;
  mode: CatalystMode;
}) {
  return (
    <sx.Answer className={clsx({ error })}>
      <CatalystIcon
        sx={{
          color: mode === "model-expert" ? "predictor.main" : "info.main",
        }}
      />
      <sx.Border>
        <sx.Body>
          <Message>{message}</Message>
          {additionalContent}
        </sx.Body>
      </sx.Border>
    </sx.Answer>
  );
}

function QuestionAndAnswerPair({
  answer,
  question,
  reverseOrder = false,
}: {
  answer: ReactNode;
  question: ReactNode;
  reverseOrder?: boolean;
}) {
  return (
    <sx.Pair data-testid={TEST_IDS.questionAndAnswer} reversed={reverseOrder}>
      {question}
      {answer}
    </sx.Pair>
  );
}

function LiteratureQuestionAndAnswerPair({
  questionAndAnswer,
}: {
  questionAndAnswer: LiteratureQuestionAndAnswer;
}) {
  const { answer, catalystMode, error, question } = questionAndAnswer;

  const uniqueMetadata = useMemo(
    () => uniqBy(answer?.document_metadata, "source"),
    [answer?.document_metadata],
  );

  return error ? (
    <QuestionAndAnswerPair
      question={<Question question={question} />}
      answer={<Answer error message={error} mode={catalystMode} />}
    />
  ) : answer ? (
    <QuestionAndAnswerPair
      question={<Question question={question} />}
      answer={
        <Answer
          additionalContent={
            answer.relevant_documents ? (
              <sx.References>
                <Typography variant="overline" component="div">
                  <FormattedMessage id="catalyst.answers.references" />
                </Typography>
                <sx.ReferencesList data-testid="reference-list">
                  {uniqueMetadata.map((metadata, index) => (
                    <sx.ReferenceItem
                      data-testid="reference-list-item"
                      key={index}
                    >
                      <AnswerReference metadata={metadata} />
                    </sx.ReferenceItem>
                  ))}
                </sx.ReferencesList>
              </sx.References>
            ) : undefined
          }
          message={answer.response}
          mode={catalystMode}
        />
      }
    />
  ) : null;
}

function ModifiedConfigModelExpertQuestionAndAnswerPairs({
  applyPredictorChange,
  hasPredictorWritePermissions,
  questionAndAnswer,
  rejectPredictorChange,
}: {
  hasPredictorWritePermissions?: boolean;
  questionAndAnswer: ModifiedConfigModelQuestionAndAnswer;
} & PredictorChangeHandlers) {
  const {
    answer,
    automaticallyDiscardedMessage,
    catalystMode,
    id,
    question,
    state,
  } = questionAndAnswer;

  if (!answer) {
    return null;
  }

  const isApplyInProgress = state === "applying";

  return (
    <>
      <QuestionAndAnswerPair
        question={<Question question={question} />}
        answer={
          <Answer
            additionalContent={
              questionAndAnswer.automaticallyDiscardedMessage ? (
                <sx.AutomaticallyDiscardedMessage variant="caption">
                  {automaticallyDiscardedMessage}
                </sx.AutomaticallyDiscardedMessage>
              ) : state === "idle" || isApplyInProgress ? (
                <sx.UserActionButtonsContainer>
                  <Button
                    data-fullstoryid="catalyst.modified-config.cancel-button"
                    data-testid={TEST_IDS.rejectModelExpertChanges}
                    disabled={isApplyInProgress}
                    onClick={() => rejectPredictorChange?.(id)}
                    size="small"
                    startIcon={<ClearIcon />}
                    variant="text"
                  >
                    <FormattedMessage id="catalyst.modified-config.cancel-button" />
                  </Button>
                  <Tooltip
                    title={
                      // `predictor` may be briefly undefined if applying changes to a
                      // trained predictor, as the version will get bumped and the
                      // PredictorEditor will be unmounted/remounted
                      hasPredictorWritePermissions || isApplyInProgress ? (
                        ""
                      ) : (
                        <FormattedMessage id="catalyst.modified-config.apply-button.write-access-required" />
                      )
                    }
                  >
                    {/* Wrapper div to enable Tooltip when Button is disabled */}
                    <div>
                      <Button
                        data-fullstoryid="catalyst.modified-config.apply-button"
                        data-testid={TEST_IDS.applyModelExpertChanges}
                        disabled={
                          isApplyInProgress || !hasPredictorWritePermissions
                        }
                        onClick={() => applyPredictorChange?.(id, answer)}
                        size="small"
                        startIcon={
                          isApplyInProgress ? (
                            <Spinner size={12} />
                          ) : (
                            <CheckIcon />
                          )
                        }
                        variant="text"
                      >
                        <FormattedMessage id="catalyst.modified-config.apply-button" />
                      </Button>
                    </div>
                  </Tooltip>
                </sx.UserActionButtonsContainer>
              ) : null
            }
            message={
              <FormattedMessage id="catalyst.modified-config.placeholder-message" />
            }
            mode={catalystMode}
          />
        }
      />
      {state === "applied" ? (
        <QuestionAndAnswerPair
          question={
            <Question
              question={
                <FormattedMessage id="catalyst.modified-config.apply" />
              }
            />
          }
          answer={
            <Answer
              message={
                <FormattedMessage id="catalyst.modified-config.changes-applied" />
              }
              mode={catalystMode}
            />
          }
        />
      ) : state === "rejected" && !automaticallyDiscardedMessage ? (
        <QuestionAndAnswerPair
          question={
            <Question
              question={
                <FormattedMessage id="catalyst.modified-config.cancel" />
              }
            />
          }
          answer={
            <Answer
              additionalContent={
                <Message>
                  <FormattedMessage id="catalyst.modified-config.how-else-can-we-help?" />
                </Message>
              }
              message={
                <FormattedMessage id="catalyst.modified-config.cancelled-answer" />
              }
              mode={catalystMode}
            />
          }
        />
      ) : null}
    </>
  );
}

function ModelQuestionAndAnswerPair({
  questionAndAnswer,
  ...modifiedConfigQuestionAndAnswerProps
}: {
  hasPredictorWritePermissions?: boolean;
  questionAndAnswer: ModelQuestionAndAnswer;
} & PredictorChangeHandlers) {
  const isModifiedConfig =
    isModifiedConfigModelQuestionAndAnswer(questionAndAnswer);
  const { answer, catalystMode, error, question } = questionAndAnswer;

  if (error) {
    return (
      <QuestionAndAnswerPair
        question={<Question question={question} />}
        answer={<Answer error message={error} mode={catalystMode} />}
      />
    );
  }

  return isMessageResponse(answer) || isUnsupportedResponse(answer) ? (
    <QuestionAndAnswerPair
      question={<Question question={question} />}
      answer={<Answer message={answer.data.message} mode={catalystMode} />}
    />
  ) : isModifiedConfig ? (
    <ModifiedConfigModelExpertQuestionAndAnswerPairs
      questionAndAnswer={questionAndAnswer}
      {...modifiedConfigQuestionAndAnswerProps}
    />
  ) : null;
}

function ModeChangeQuestionAndAnswerPair({
  allQuestionsAndAnswers,
  questionAndAnswer,
}: {
  allQuestionsAndAnswers: CatalystQuestionAndAnswer[];
  questionAndAnswer: ModeChangeStatement;
}) {
  const isFirstModelExpertModeChange = useMemo(
    () =>
      questionAndAnswer.id ===
      allQuestionsAndAnswers.find(
        (qAndA) =>
          isModeChangeStatement(qAndA) && qAndA.catalystMode === "model-expert",
      )?.id,
    [allQuestionsAndAnswers, questionAndAnswer],
  );

  return (
    <QuestionAndAnswerPair
      question={
        <sx.ModeChangeStatement>
          <sx.ModeChangeStatementText component="div" variant="overline">
            <FormattedMessage
              id="catalyst.now-communicating-with-{mode}"
              values={{
                mode: (
                  <FormattedMessage
                    id={`catalyst.${questionAndAnswer.catalystMode}`}
                  />
                ),
              }}
            />
          </sx.ModeChangeStatementText>
        </sx.ModeChangeStatement>
      }
      answer={
        <Answer
          message={
            isFirstModelExpertModeChange ? (
              <sx.ModelExpertWelcomePrompt
                data-testid={TEST_IDS.modelExpertWelcomePrompt}
              >
                <sx.ModelExpertWelcomePromptLine>
                  <FormattedMessage id="catalyst.model-expert.first-usage.intro" />
                </sx.ModelExpertWelcomePromptLine>
                <FormattedMessage
                  id="catalyst.model-expert.first-usage.sample-prompts"
                  values={{
                    sample: (content) => (
                      <sx.ModelExpertWelcomePromptLine isSamplePrompt>
                        {content}
                      </sx.ModelExpertWelcomePromptLine>
                    ),
                  }}
                />
                <sx.ModelExpertWelcomePromptLine>
                  <FormattedMessage id="catalyst.model-expert.question-placeholder" />
                </sx.ModelExpertWelcomePromptLine>
              </sx.ModelExpertWelcomePrompt>
            ) : (
              <FormattedMessage
                id={`catalyst.${questionAndAnswer.catalystMode}.question-placeholder`}
              />
            )
          }
          mode={questionAndAnswer.catalystMode}
        />
      }
    />
  );
}

function SwitchToModelExpertPromptQuestionAndAnswerPair({
  onDeclineSwitchToModelExpert,
  onSwitchToModelExpert,
  questionAndAnswer,
}: {
  questionAndAnswer: SwitchToModelExpertPrompt;
} & SwitchToModelExpertPromptHandlers) {
  const { automaticallyResolvedMessage, catalystMode, id, state } =
    questionAndAnswer;

  return (
    <QuestionAndAnswerPair
      reverseOrder
      question={
        state === "yes" ? (
          <Question
            question={
              <FormattedMessage id="catalyst.use-model-expert-prompt.yes-answer" />
            }
          />
        ) : state === "no" ? (
          <Question
            question={
              <FormattedMessage id="catalyst.use-model-expert-prompt.no-answer" />
            }
          />
        ) : null
      }
      answer={
        <Answer
          additionalContent={
            automaticallyResolvedMessage ? (
              <sx.AutomaticallyDiscardedMessage variant="caption">
                {automaticallyResolvedMessage}
              </sx.AutomaticallyDiscardedMessage>
            ) : state === "idle" ? (
              <sx.UserActionButtonsContainer>
                <Button
                  data-fullstoryid="catalyst.switch-to-model-expert.decline-button"
                  data-testid={TEST_IDS.switchToModelExpertPromptDeclineButton}
                  onClick={() => onDeclineSwitchToModelExpert(id)}
                  size="small"
                  startIcon={<ClearIcon />}
                  variant="text"
                >
                  <FormattedMessage id="catalyst.use-model-expert-prompt.no-button" />
                </Button>
                <Button
                  data-fullstoryid="catalyst.switch-to-model-expert.accept-button"
                  data-testid={TEST_IDS.switchToModelExpertPromptAcceptButton}
                  onClick={() => onSwitchToModelExpert(id)}
                  size="small"
                  startIcon={<CheckIcon />}
                  variant="text"
                >
                  <FormattedMessage id="catalyst.use-model-expert-prompt.yes-button" />
                </Button>
              </sx.UserActionButtonsContainer>
            ) : null
          }
          message={
            <FormattedMessage id="catalyst.use-model-expert-prompt.question" />
          }
          mode={catalystMode}
        />
      }
    />
  );
}

export function QuestionAndAnswer({
  allQuestionsAndAnswers,
  applyPredictorChange,
  hasPredictorWritePermissions,
  onDeclineSwitchToModelExpert,
  onSwitchToModelExpert,
  questionAndAnswer,
  rejectPredictorChange,
}: {
  allQuestionsAndAnswers: CatalystQuestionAndAnswer[];
  hasPredictorWritePermissions?: boolean;
  questionAndAnswer: CatalystQuestionAndAnswer;
} & PredictorChangeHandlers &
  SwitchToModelExpertPromptHandlers) {
  return isModeChangeStatement(questionAndAnswer) ? (
    <ModeChangeQuestionAndAnswerPair
      allQuestionsAndAnswers={allQuestionsAndAnswers}
      questionAndAnswer={questionAndAnswer}
    />
  ) : isLiteratureQuestionAndAnswer(questionAndAnswer) ? (
    <LiteratureQuestionAndAnswerPair questionAndAnswer={questionAndAnswer} />
  ) : isModelQuestionAndAnswer(questionAndAnswer) ? (
    <ModelQuestionAndAnswerPair
      applyPredictorChange={applyPredictorChange}
      hasPredictorWritePermissions={hasPredictorWritePermissions}
      questionAndAnswer={questionAndAnswer}
      rejectPredictorChange={rejectPredictorChange}
    />
  ) : isSwitchToModelExpertPrompt(questionAndAnswer) ? (
    <SwitchToModelExpertPromptQuestionAndAnswerPair
      questionAndAnswer={questionAndAnswer}
      onDeclineSwitchToModelExpert={onDeclineSwitchToModelExpert}
      onSwitchToModelExpert={onSwitchToModelExpert}
    />
  ) : null;
}

export function QuestionsAndAnswers({
  data,
  ...passthroughProps
}: {
  applyInProgressId?: string;
  data: CatalystQuestionAndAnswer[];
  hasPredictorWritePermissions?: boolean;
} & PredictorChangeHandlers &
  SwitchToModelExpertPromptHandlers) {
  return (
    <>
      {data.map((questionAndAnswer) => (
        <QuestionAndAnswer
          key={questionAndAnswer.id}
          allQuestionsAndAnswers={data}
          questionAndAnswer={questionAndAnswer}
          {...passthroughProps}
        />
      ))}
    </>
  );
}
